import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { SignalRService, SignalRConnectionState } from '@/common/infrastructure/services/SignalRService';
import { getSignalRConfig } from '@/common/infrastructure/config/signalRConfig';
import { isCorsError, shouldRetryCorsError } from '@/common/infrastructure/utils/signalRErrorUtils';

/**
 * Shallow comparison for SignalR connection state
 */
export const shallowEqualState = (a: SignalRConnectionState, b: SignalRConnectionState): boolean =>
  a.isConnected === b.isConnected &&
  a.isConnecting === b.isConnecting &&
  a.isReconnecting === b.isReconnecting &&
  a.connectionId === b.connectionId &&
  a.error === b.error;

/**
 * Check if SignalR URL is configured
 */
const isSignalRConfigured = (): boolean => {
  return !!import.meta.env.VITE_SIGNALR_URL;
};

/**
 * Get SignalR configuration error message
 */
const getConfigErrorMessage = (): string => {
  return 'SignalR URL not configured. Please set VITE_SIGNALR_URL environment variable.';
};

/**
 * Options for useSignalRConnection hook
 */
export interface UseSignalRConnectionOptions {
  enabled?: boolean;
  onConnected?: () => void;
  onDisconnected?: (error?: Error) => void;
  onReconnecting?: (error?: Error) => void;
  onError?: (error: unknown) => void;
}

/**
 * Shared hook for SignalR connection management
 * Handles initialization, connection, and state management
 */
export const useSignalRConnection = (options: UseSignalRConnectionOptions = {}) => {
  const { enabled = true, onConnected, onDisconnected, onReconnecting, onError } = options;
  const [connectionState, setConnectionState] = useState<SignalRConnectionState>({
    isConnected: false,
    isConnecting: false,
    isReconnecting: false
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signalRServiceRef = useRef<SignalRService | null>(null);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateChangeListenerRef = useRef<((state: SignalRConnectionState) => void) | null>(null);
  const isInitializedRef = useRef(false);
  const hasCorsErrorRef = useRef(false);
  const retryCountRef = useRef(0);
  const lastRetryTimeRef = useRef(0);
  const idleCallbackIdRef = useRef<number | null>(null);

  // Store callbacks in refs to avoid dependency issues
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  const onReconnectingRef = useRef(onReconnecting);
  const onErrorRef = useRef(onError);

  // Keep refs up to date
  useEffect(() => {
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
    onReconnectingRef.current = onReconnecting;
    onErrorRef.current = onError;
  }, [onConnected, onDisconnected, onReconnecting, onError]);

  const stableOnConnected = useEffectEvent(() => {
    // Reset CORS error state on successful connection
    hasCorsErrorRef.current = false;
    retryCountRef.current = 0;
    lastRetryTimeRef.current = 0;
    onConnectedRef.current?.();
  });

  const stableOnDisconnected = useEffectEvent((err?: Error) => {
    onDisconnectedRef.current?.(err);
  });

  const stableOnReconnecting = useEffectEvent((err?: Error) => {
    onReconnectingRef.current?.(err);
  });

  const stableOnError = useEffectEvent((err: unknown) => {
    if (isCorsError(err)) {
      hasCorsErrorRef.current = true;
      lastRetryTimeRef.current = Date.now();
    }
    onErrorRef.current?.(err);
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!isSignalRConfigured()) {
      const errorMsg = getConfigErrorMessage();
      setError(errorMsg);
      console.warn(errorMsg);
      return;
    }

    // Early return if already initialized and connected
    const currentService = SignalRService.getInstance(getSignalRConfig());
    if (isInitializedRef.current && currentService.isConnected()) {
      signalRServiceRef.current = currentService;
      return;
    }

    let isAborted = false;
    let isInitializing = false;

    const initializeConnection = async () => {
      // Prevent multiple simultaneous initialization attempts
      if (isInitializing) {
        return;
      }

      try {
        isInitializing = true;
        setError(null);

        // Double-check if already connected (race condition protection)
        if (isInitializedRef.current && signalRServiceRef.current?.isConnected()) {
          isInitializing = false;
          return;
        }

        // Check CORS retry logic before attempting connection
        if (hasCorsErrorRef.current) {
          const { shouldRetry, delayMs } = shouldRetryCorsError(retryCountRef.current, lastRetryTimeRef.current);

          if (!shouldRetry) {
            if (delayMs > 0) {
              console.warn(`SignalR: CORS error detected. Waiting ${delayMs}ms before retry...`);
            } else {
              const errorMessage = 'CORS error: Unable to connect to SignalR. Please check backend CORS configuration.';
              setError(errorMessage);
              console.error('SignalR: Too many CORS errors. Stopping retries.');
            }
            return;
          }

          retryCountRef.current += 1;
          lastRetryTimeRef.current = Date.now();
        }

        const config = getSignalRConfig();
        const signalRService = SignalRService.getInstance(config);
        signalRServiceRef.current = signalRService;

        if (isAborted) return;

        signalRService.acquire();

        // Remove previous listener if it exists
        if (stateChangeListenerRef.current) {
          signalRService.offConnectionStateChange(stateChangeListenerRef.current);
        }

        // Create and store new listener
        const stateChangeListener = (state: SignalRConnectionState) => {
          if (isAborted) return;
          setConnectionState((prev) => (shallowEqualState(prev, state) ? prev : state));

          // Handle connection state callbacks
          if (state.isConnected) {
            stableOnConnected();
          } else if (state.isReconnecting) {
            stableOnReconnecting(state.error ? new Error(state.error) : undefined);
          } else if (!state.isConnected && !state.isConnecting && !state.isReconnecting) {
            stableOnDisconnected(state.error ? new Error(state.error) : undefined);
          }
        };

        stateChangeListenerRef.current = stateChangeListener;
        signalRService.onConnectionStateChange(stateChangeListener);

        await signalRService.initialize();
        if (isAborted) return;
        isInitializedRef.current = true;
        setIsInitialized(true);

        await signalRService.connect();
        if (isAborted) {
          isInitializing = false;
          return;
        }

        // Note: CORS state reset happens in stableOnConnected() which is called
        // via the connection state change handler when isConnected becomes true
        isInitializing = false;
      } catch (err) {
        isInitializing = false;
        if (isAborted) return;

        if (isCorsError(err)) {
          hasCorsErrorRef.current = true;
          lastRetryTimeRef.current = Date.now();
          const errorMessage = 'CORS error: Unable to connect to SignalR. The backend may not be configured to allow credentials.';
          setError(errorMessage);
          console.error('SignalR CORS error:', err);
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          setError(errorMessage);
          console.error('Failed to initialize SignalR connection:', err);
        }

        stableOnError(err);
      }
    };

    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
    }
    if (idleCallbackIdRef.current !== null) {
      (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback?.(idleCallbackIdRef.current);
      idleCallbackIdRef.current = null;
    }

    const startDelayMs = import.meta.env?.MODE === 'test' ? 0 : 400;
    const scheduleInitialization = () => {
      startTimeoutRef.current = setTimeout(() => {
        if (!isAborted) {
          initializeConnection();
        }
      }, startDelayMs);
    };

    if (typeof window !== 'undefined') {
      const requestIdle = (window as Window & { requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number })
        .requestIdleCallback;
      if (typeof requestIdle === 'function') {
        idleCallbackIdRef.current = requestIdle(
          () => {
            scheduleInitialization();
          },
          { timeout: 1500 }
        );
      } else {
        scheduleInitialization();
      }
    } else {
      scheduleInitialization();
    }

    return () => {
      isAborted = true;
      isInitializedRef.current = false;
      if (signalRServiceRef.current) {
        // Remove state change listener
        if (stateChangeListenerRef.current) {
          signalRServiceRef.current.offConnectionStateChange(stateChangeListenerRef.current);
          stateChangeListenerRef.current = null;
        }
        signalRServiceRef.current.release();
      }
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }
      if (idleCallbackIdRef.current !== null) {
        (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback?.(idleCallbackIdRef.current);
        idleCallbackIdRef.current = null;
      }
    };
  }, [enabled]); // useEffectEvent callbacks are stable and don't need to be in deps

  // Reset CORS error state when enabled changes
  useEffect(() => {
    if (enabled && hasCorsErrorRef.current) {
      hasCorsErrorRef.current = false;
      retryCountRef.current = 0;
      lastRetryTimeRef.current = 0;
    }
  }, [enabled]);

  // Compute final error message
  const finalError =
    hasCorsErrorRef.current && retryCountRef.current > 5
      ? 'CORS error: Unable to connect to SignalR. Please check backend CORS configuration.'
      : error;

  return {
    connectionState,
    isInitialized,
    error: finalError,
    isConnected: connectionState.isConnected,
    connectionId: connectionState.connectionId,
    signalRService: signalRServiceRef.current
  };
};
